import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { useResponsive, useRoleNavigation, useAutoTranslate } from '../../hooks';
import { formatPct } from '../../utils/formatPercentage';
import { useRoute, useNavigation, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle, G } from 'react-native-svg';
import { initiativesApi, kpisApi, activitiesApi, initiativeCommentsApi, InitiativeComment } from '../../services/apiService';
import { Initiative, KPIList, ActivityList } from '../../types';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import DeletionApprovalBanner from '../../components/DeletionApprovalBanner';
import { COLORS, FONTS, LABELS } from '../../constants/config';
import {
  LoadingScreen,
  EmptyState,
  PageContainer,
  Navbar,
  CommentSection,
  CommentData,
  getInitialsFromUserType,
  getAvatarColorFromUserType,
  UserType,
  ViewOnlyBadge,
  Breadcrumb,
} from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';
import { getCachedData, setCachedData } from '../../utils/dataCache';

type RouteProps = RouteProp<MainStackParamList, 'InitiativeDetail'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EyeIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M2.06153 12.3474C1.97819 12.1229 1.97819 11.8759 2.06153 11.6514C2.87323 9.68324 4.25104 8.00042 6.0203 6.81628C7.78955 5.63214 9.87057 5 11.9995 5C14.1285 5 16.2095 5.63214 17.9788 6.81628C19.748 8.00042 21.1258 9.68324 21.9375 11.6514C22.0209 11.8759 22.0209 12.1229 21.9375 12.3474C21.1258 14.3155 19.748 15.9983 17.9788 17.1825C16.2095 18.3666 14.1285 18.9988 11.9995 18.9988C9.87057 18.9988 7.78955 18.3666 6.0203 17.1825C4.25104 15.9983 2.87323 14.3155 2.06153 12.3474Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 20, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EditIcon = ({ size = 20, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V12" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.3751 2.62523C18.7729 2.2274 19.3125 2.00391 19.8751 2.00391C20.4377 2.00391 20.9773 2.2274 21.3751 2.62523C21.7729 3.02305 21.9964 3.56262 21.9964 4.12523C21.9964 4.68784 21.7729 5.2274 21.3751 5.62523L12.3621 14.6392C12.1246 14.8765 11.8313 15.0501 11.5091 15.1442L8.63609 15.9842C8.55005 16.0093 8.45883 16.0108 8.372 15.9886C8.28517 15.9663 8.20592 15.9212 8.14254 15.8578C8.07916 15.7944 8.03398 15.7151 8.01174 15.6283C7.98949 15.5415 7.991 15.4503 8.01609 15.3642L8.85609 12.4912C8.95062 12.1693 9.12463 11.8763 9.36209 11.6392L18.3751 2.62523Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ArrowRightIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M5 12H19M19 12L12 5M19 12L12 19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const SendIcon = ({ size = 16, color = '#FFFFFF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M22 2L11 13" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M22 2L15 22L11 13L2 9L22 2Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Circular Gauge Component
// ============================================================

interface CircularGaugeProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  backgroundColor?: string;
}

const CircularGauge = ({
  percentage,
  size = 120,
  strokeWidth = 12,
  color = '#2CA02C',
  backgroundColor = '#E5E7EB'
}: CircularGaugeProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {percentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={color}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      <View style={{ position: 'absolute', alignItems: 'center' }}>
        <Text style={{ fontSize: size > 100 ? 24 : 16, fontFamily: FONTS.numbers, fontWeight: '500', color: '#414042' }}>
          %{percentage}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Donut Chart Component for Status Distribution - Figma Design
// ============================================================

interface PieChartProps {
  excellent: number; // Green
  good: number; // Orange
  needsImprovement: number; // Red
  size?: number;
}

const PieChart = ({ excellent, good, needsImprovement, size = 120 }: PieChartProps) => {
  const total = excellent + good + needsImprovement || 1;
  const center = size / 2;
  const outerRadius = size / 2 - 5;
  const innerRadius = outerRadius * 0.6; // Donut hole
  const strokeWidth = outerRadius - innerRadius;
  const radius = (outerRadius + innerRadius) / 2;
  const circumference = 2 * Math.PI * radius;

  // Calculate stroke dash arrays for each segment
  const excellentPercent = excellent / total;
  const goodPercent = good / total;
  const needsImprovementPercent = needsImprovement / total;

  const excellentDash = circumference * excellentPercent;
  const goodDash = circumference * goodPercent;
  const needsImprovementDash = circumference * needsImprovementPercent;

  // Calculate rotation offsets (start from top, go clockwise)
  const excellentRotation = -90;
  const goodRotation = excellentRotation + (excellentPercent * 360);
  const needsImprovementRotation = goodRotation + (goodPercent * 360);

  return (
    <Svg width={size} height={size}>
      {/* Excellent - Green */}
      {excellent > 0 && (
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#22C55E"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${excellentDash} ${circumference}`}
          strokeDashoffset={0}
          rotation={excellentRotation}
          origin={`${center}, ${center}`}
        />
      )}
      {/* Good - Orange */}
      {good > 0 && (
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#F97316"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${goodDash} ${circumference}`}
          strokeDashoffset={0}
          rotation={goodRotation}
          origin={`${center}, ${center}`}
        />
      )}
      {/* Needs Improvement - Red */}
      {needsImprovement > 0 && (
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#EF4444"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${needsImprovementDash} ${circumference}`}
          strokeDashoffset={0}
          rotation={needsImprovementRotation}
          origin={`${center}, ${center}`}
        />
      )}
    </Svg>
  );
};

// ============================================================
// KPI Card Component
// ============================================================

interface KPICardProps {
  id?: number;
  title: string;
  achievementPercentage: number;
  weight?: string;
  onViewDetails?: (id: number) => void;
  cardWidth?: number;
  t: (key: string) => string;
  translate?: (text: string | undefined | null) => string;
}

const KPICard = ({ id, title, achievementPercentage, weight, onViewDetails, cardWidth, t, translate }: KPICardProps) => {
  const { colors, isDark } = useTheme();
  const displayTitle = translate ? translate(title) : title;
  // Use achievement_percentage from API (نسبة الإنجاز) - no cap
  const percentage = achievementPercentage;

  // Color rules: >= 100% Green, >= 80% Orange, < 80% Red
  const getStatusColor = () => {
    if (percentage >= 100) return '#22C55E';
    if (percentage >= 80) return '#F97316';
    return '#EF4444';
  };

  const statusColor = getStatusColor();

  // Display نسبة الإنجاز percentage
  const displayValue = `% ${percentage}`;

  return (
    <View style={[styles.kpiCard, cardWidth && { width: cardWidth }, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      <Text style={[styles.kpiCardTitle, { color: colors.textPrimary }]}>{displayTitle}</Text>

      {/* Weight badge */}
      {weight && (
        <View style={[styles.kpiWeightRow]}>
          <Text style={[styles.kpiWeightLabel, { color: colors.textMuted }]}>{t('common.weight')}</Text>
          <View style={[styles.kpiWeightBadge, { backgroundColor: colors.backgroundAlt }]}>
            <Text style={[styles.kpiWeightValue, { color: colors.textPrimary }]}>% {weight}</Text>
          </View>
        </View>
      )}

      <View style={styles.kpiValueRow}>
        <View style={[styles.kpiValueBadge, { backgroundColor: colors.backgroundAlt }]}>
          <Text style={[styles.kpiValue, { color: statusColor }]}>{displayValue}</Text>
        </View>

        <View style={styles.kpiProgressContainer}>
          <View style={[styles.kpiProgressTrack, { backgroundColor: isDark ? '#374151' : '#E5E7EB' }]}>
            <View style={[styles.kpiProgressFill, { width: `${Math.min(percentage, 100)}%`, backgroundColor: statusColor }]} />
            <View style={[styles.kpiProgressDot, { backgroundColor: statusColor, right: `${Math.min(percentage, 100)}%`, transform: [{ translateX: 7 }] }]} />
          </View>
        </View>
      </View>

      <TouchableOpacity
        style={styles.kpiDetailsLink}
        onPress={() => id && onViewDetails?.(id)}
        disabled={!id}
      >
        <ChevronLeftIcon size={16} color={id ? colors.textPrimary : colors.textMuted} />
        <Text style={[styles.kpiDetailsText, { color: id ? colors.textPrimary : colors.textMuted }]}>{t('common.viewDetails')}</Text>
      </TouchableOpacity>
    </View>
  );
};

// ============================================================
// Activity Card Component
// ============================================================

interface ActivityCardProps {
  activity: ActivityList;
  onViewDetails?: (id: number) => void;
  cardWidth?: number;
  t: (key: string) => string;
  translate?: (text: string | undefined | null) => string;
}

const ActivityCard = ({ activity, onViewDetails, cardWidth, t, translate }: ActivityCardProps) => {
  const { colors, isDark } = useTheme();
  const displayName = translate ? translate(activity.name) : activity.name;

  const percentage = parseFloat(Number(activity.achievement_percentage ?? 0).toFixed(2));
  // Color rules: >= 100% Green, >= 80% Orange, < 80% Red
  const statusColor = percentage >= 100 ? '#22C55E' : percentage >= 80 ? '#F97316' : '#EF4444';
  const displayValue = `% ${formatPct(percentage)}`;

  return (
    <View style={[styles.activityCard, cardWidth && { width: cardWidth }, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      <Text style={[styles.activityCardTitle, { color: colors.textPrimary }]}>{displayName}</Text>

      <View style={styles.kpiValueRow}>
        <View style={[styles.kpiValueBadge, { backgroundColor: colors.backgroundAlt }]}>
          <Text style={[styles.kpiValue, { color: colors.textPrimary }]}>{displayValue}</Text>
        </View>

        <View style={styles.kpiProgressContainer}>
          <View style={[styles.kpiProgressTrack, { backgroundColor: isDark ? '#374151' : '#E5E7EB' }]}>
            <View style={[styles.kpiProgressFill, { width: `${percentage}%`, backgroundColor: statusColor }]} />
            <View style={[styles.kpiProgressDot, { backgroundColor: statusColor, right: `${percentage}%`, transform: [{ translateX: 7 }] }]} />
          </View>
        </View>
      </View>

      <TouchableOpacity
        style={styles.kpiDetailsLink}
        onPress={() => activity.id && onViewDetails?.(activity.id)}
        disabled={!activity.id}
      >
        <ChevronLeftIcon size={16} color={activity.id ? colors.textPrimary : colors.textMuted} />
        <Text style={[styles.kpiDetailsText, { color: activity.id ? colors.textPrimary : colors.textMuted }]}>{t('common.viewDetails')}</Text>
      </TouchableOpacity>
    </View>
  );
};


// ============================================================
// Main Screen
// ============================================================

export default function InitiativeDetailScreen() {
  const route = useRoute<RouteProps>();
  const navigation = useNavigation<NavigationProp>();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { navigateToHome, isViewOnly, canComment } = useRoleNavigation();
  const { id } = route.params;
  const { isMobile, isTablet, isDesktop, isSmallMobile, horizontalCardWidth, containerPadding, cardPadding, sectionSpacing } = useResponsive();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const { translate, translateOrg } = useAutoTranslate();
  const { selectedYear } = useYear();

  // Theme colors object
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
  };

  // Check if user is strategy_team (they can't add activities)
  const isStrategyTeam = (user?.permissions?.includes('hrcpms.strategic_goals.create') ?? false) || (user?.is_superuser ?? false);
  // Proposing operational items (activities / KPIs on a عنصر تشغيلي) is governed
  // by `hrcpms.executive_items.create`, which Section Heads (رئيس قسم) hold. A
  // Strategy Section Head also has `strategic_goals.create`, so the coarse
  // isStrategyTeam/isViewOnly flags wrongly hid the Add buttons from them.
  const canCreateOperationalItem = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.executive_items.create')
  );
  // DTS 2.2: only Strategy Team (hrcpms.soft_delete) can delete approved
  // executive items. Section Head can withdraw their own proposal while it
  // is still in draft / pending review — once approval_status === 'approved'
  // the delete affordance disappears for them.
  const canSoftDelete = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.soft_delete')
  );

  // 3-stage approval workflow visibility helpers (DTS UC-03)
  const isManager = user?.position_type === 'MANAGER';
  const isDirector = user?.position_type === 'DIRECTOR';
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [rejectModalKind, setRejectModalKind] = useState<null | 'director' | 'strategy'>(null);
  const [rejectComment, setRejectComment] = useState('');

  // Responsive breakpoints (for backward compatibility)
  const isSmallScreen = isMobile;

  // Cached loading state - shows cached data instantly on revisit.
  // Declared BEFORE the state below because `initiative`/`loading` seed from it.
  const cachedInit = useRef(getCachedData<any>(`initiative-${id}`)).current;

  const [initiative, setInitiative] = useState<Initiative | null>(cachedInit);
  const [kpis, setKpis] = useState<KPIList[]>([]);
  const [activities, setActivities] = useState<ActivityList[]>([]);
  const [apiComments, setApiComments] = useState<InitiativeComment[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(!cachedInit);
  const isOrgEmployee = user?.position_type === 'EMPLOYEE';
  const canApproveDeletion = (user?.is_superuser || user?.position_type === 'MANAGER' || user?.position_type === 'DIRECTOR' || (user?.permissions?.includes('hrcpms.strategic_goals.create') ?? false));

  // Comments state - unified with GoalDetailScreen pattern
  const [showAddComment, setShowAddComment] = useState(false);
  const [editingComment, setEditingComment] = useState<CommentData | null>(null);


  // Convert API comment to CommentData format for display (same as GoalDetailScreen)
  const convertApiComment = (apiComment: InitiativeComment): CommentData => {
    // Get the actual author name from author_details
    const fullName = apiComment.author_details?.full_name || apiComment.author_details?.username || t('common.user');
    const nameParts = fullName.split(' ').filter(Boolean);
    const authorName = nameParts[0] || t('common.user'); // First name only

    // Generate initials from the actual name
    const initials = nameParts.length >= 2
      ? `${nameParts[0][0]} ${nameParts[1][0]}`
      : fullName.slice(0, 2);

    const isOwner = user?.id === apiComment.author;
    const userType = (apiComment.author_type || 'org_employee') as UserType;
    const avatarColor = getAvatarColorFromUserType(userType);

    return {
      id: apiComment.id,
      name: authorName,
      initials,
      date: new Date(apiComment.created_at).toLocaleDateString('ar-SA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      text: apiComment.content,
      avatarColor,
      showMenu: isOwner, // Only show menu for own comments
      isEdited: apiComment.is_edited,
      originalText: apiComment.original_content,
      userType,
    };
  };

  // Convert API comments to display format
  const comments = apiComments.map(convertApiComment);

  const fetchComments = useCallback(async () => {
    try {
      const commentsData = await initiativeCommentsApi.getForInitiative(id);
      setApiComments(commentsData);
    } catch {
      setApiComments([]);
    }
  }, [id]);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const data = await initiativesApi.get(id);

      setInitiative(data);
      setCachedData(`initiative-${id}`, data);

      // Use KPIs and activities from initiative response (already filtered consistently with kpi_distribution)
      // This ensures the KPIs/activities lists match the distribution data
      setKpis(data.kpis || []);
      setActivities(data.activities || []);

      // Fetch comments for this initiative
      await fetchComments();
    } catch (err: any) {
      setError(err.message || t('messages.error.loadFailed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, fetchComments]);

  // 3-stage approval action handlers
  const handleApprovalAction = useCallback(
    async (
      action: 'submit' | 'directorApprove' | 'directorReject' | 'strategyApprove' | 'strategyReject',
      comment?: string,
    ) => {
      if (approvalBusy) return;
      setApprovalBusy(true);
      try {
        let updated: Initiative | null = null;
        if (action === 'submit') {
          updated = await initiativesApi.submitForApproval(id);
          showSuccess('تم إرسال العنصر لمدير الإدارة');
        } else if (action === 'directorApprove') {
          updated = await initiativesApi.directorApprove(id, comment);
          showSuccess('تم الاعتماد، أُرسل لفريق الاستراتيجية');
        } else if (action === 'directorReject') {
          updated = await initiativesApi.directorReject(id, comment || '');
          showSuccess('تم الرفض');
        } else if (action === 'strategyApprove') {
          updated = await initiativesApi.strategyApprove(id, comment);
          showSuccess('تم الاعتماد النهائي');
        } else if (action === 'strategyReject') {
          updated = await initiativesApi.strategyReject(id, comment || '');
          showSuccess('تم الرفض');
        }
        if (updated) {
          setInitiative(updated);
          setCachedData(`initiative-${id}`, updated);
        }
      } catch (err: any) {
        const detail = err?.response?.data?.detail || 'تعذّر تنفيذ الإجراء';
        showError(detail);
      } finally {
        setApprovalBusy(false);
      }
    },
    [approvalBusy, id, showSuccess, showError],
  );

  const submitRejection = useCallback(
    async () => {
      if (!rejectComment.trim()) {
        showError('يجب كتابة سبب الرفض');
        return;
      }
      const action = rejectModalKind === 'director' ? 'directorReject' : 'strategyReject';
      await handleApprovalAction(action, rejectComment.trim());
      setRejectModalKind(null);
      setRejectComment('');
    },
    [rejectModalKind, rejectComment, handleApprovalAction, showError],
  );

  // Auto-refresh when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  // Auto-refresh data when screen comes into focus (e.g., after adding activity/KPI)
  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  // Refetch when year changes
  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleKPIViewDetails = (kpiId: number) => {
    navigation.navigate('KPIDetail', { id: kpiId });
  };

  const handleActivityViewDetails = (activityId: number) => {
    navigation.navigate('ActivityDetail', { id: activityId });
  };

  // Comment handlers - unified with GoalDetailScreen pattern
  const handleAddComment = async (text: string) => {
    try {
      // Determine author_type based on user profile
      const authorType = (user?.permissions?.includes('hrcpms.strategic_goals.create') ?? false) ? 'strategy_team' : 'org_employee';

      await initiativeCommentsApi.create({
        initiative: id,
        content: text,
        comment_type: 'note',
        author_type: authorType,
      });
      fetchComments();
      setShowAddComment(false);
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleEditComment = (comment: CommentData) => {
    setEditingComment(comment);
    setShowAddComment(true);
  };

  const handleUpdateComment = async (text: string) => {
    if (editingComment) {
      try {
        await initiativeCommentsApi.update(editingComment.id, { content: text });
        fetchComments();
      } catch (error) {
        console.error('Error updating comment:', error);
      } finally {
        setEditingComment(null);
        setShowAddComment(false);
      }
    }
  };

  const handleDeleteInitiative = () => { setShowDeletionImpact(true); };
  const confirmDeleteInitiative = async () => {
    try {
      setIsDeleting(true);
      const response = await initiativesApi.delete(id);
      if ((response as any)?.status === 202) { showSuccess(t('deletion.requestSent')); }
      else { showSuccess(t('initiatives.deleteSuccess')); }
      navigation.goBack();
    } catch (error: any) {
      if (error.response?.status === 202) { showSuccess(t('deletion.requestSent')); navigation.goBack(); }
      else if (error.response?.status === 409) { showError(t('deletion.alreadyPending')); }
      else { showError(t('messages.deleteFailed')); }
    } finally { setIsDeleting(false); setShowDeletionImpact(false); }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      const response = await initiativeCommentsApi.delete(commentId);
      // Check if it was a delete request (org_employee) or direct delete
      fetchComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleCancelComment = () => {
    setShowAddComment(false);
    setEditingComment(null);
  };

  const handleCommentSubmit = (text: string) => {
    if (editingComment) {
      handleUpdateComment(text);
    } else {
      handleAddComment(text);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error || !initiative) {
    return (
      <EmptyState
        icon="alert-circle-outline"
        title={LABELS.error}
        message={error || t('initiatives.notFound')}
        actionLabel={LABELS.refresh}
        onAction={fetchData}
      />
    );
  }

  // Helper function for achievement-based colors
  const getAchievementColor = (percentage: number): string => {
    if (percentage >= 100) return '#22C55E';  // Green - Excellent
    if (percentage >= 80) return '#F97316';   // Orange - Good
    return '#EF4444';                          // Red - Needs Improvement
  };

  // Use kpi_distribution from API response instead of local calculation
  const distribution = initiative?.kpi_distribution ?? { excellent: 0, good: 0, needs_improvement: 0, total: 0 };
  const totalKpis = distribution.total ?? kpis.length;
  const excellentPercent = distribution.excellent ?? 0;
  const goodPercent = distribution.good ?? 0;
  const needsImprovementPercent = distribution.needs_improvement ?? 0;

  // Prefer the immediate-feedback value (avg of latest readings, ignoring
  // measurement schedules) so a user who just entered a reading sees their
  // progress right away. Fall back to the cumulative stage-based value
  // when no readings exist yet.
  const avgPerformance =
    (initiative as any)?.latest_achievement_percentage
    ?? initiative?.achievement_percentage
    ?? null;

  // deviation_percentage is null when no measurement periods are due yet
  const deviation = initiative?.deviation_percentage ?? null;

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: containerPadding }
        ]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('navigation.initiatives'), route: 'Initiatives' },
            { label: translate(initiative.title) },
          ]}
        />

        {/* Title Section - Figma Design */}
        <View style={[styles.titleSectionCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          {!isViewOnly ? (
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {(canSoftDelete || initiative.approval_status !== 'approved') && (
                <TouchableOpacity
                  style={[styles.editIconButton, { minHeight: 44, minWidth: 44, backgroundColor: '#FEE2E2', borderColor: '#FECACA' }]}
                  onPress={handleDeleteInitiative}
                >
                  <TrashIcon size={15} color="#DC2626" />
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={[styles.editIconButton, { minHeight: 44, minWidth: 44 }]}
                onPress={() => navigation.navigate('AddInitiative', { initiativeId: id })}
              >
                <EditIcon size={15} color="#414042" />
              </TouchableOpacity>
            </View>
          ) : null}
          <View style={styles.titleTextContainer}>
            <Text style={[styles.pageTitle, { color: themeColors.textPrimary }]}>{translate(initiative.title)}</Text>

            {/* Type chip — DTS uses 5 operational element types; show the
                specific one so a "project" doesn't read as "initiative". */}
            {(() => {
              const typeLabels: Record<string, string> = {
                initiative: 'مبادرة',
                project: 'مشروع',
                program: 'برنامج',
                operational_activity: 'نشاط تشغيلي',
                process: 'عملية',
              };
              const t = (initiative as any).initiative_type;
              const label = typeLabels[t] || (initiative as any).initiative_type_display || 'عنصر تشغيلي';
              return (
                <View style={{
                  alignSelf: 'flex-end',
                  marginTop: 6,
                  paddingHorizontal: 10,
                  paddingVertical: 4,
                  borderRadius: 12,
                  backgroundColor: '#EFF6FF',
                  borderWidth: 1,
                  borderColor: '#3B82F6',
                }}>
                  <Text style={{ color: '#1E40AF', fontSize: 12, fontFamily: FONTS.medium }}>
                    {label}
                  </Text>
                </View>
              );
            })()}

            {/* Status Badge — DTS 3-stage approval workflow */}
            {initiative.approval_status === 'pending' && (
              <View style={styles.draftStatusBadge}>
                <Text style={styles.draftStatusText}>مسودة</Text>
              </View>
            )}
            {initiative.approval_status === 'pending_director' && (
              <View style={styles.pendingStatusBadge}>
                <Text style={styles.pendingStatusText}>بانتظار اعتماد مدير الإدارة</Text>
              </View>
            )}
            {initiative.approval_status === 'pending_strategy' && (
              <View style={styles.pendingStatusBadge}>
                <Text style={styles.pendingStatusText}>بانتظار الاعتماد النهائي</Text>
              </View>
            )}
            {initiative.approval_status === 'rejected' && (
              <View style={styles.rejectedStatusBadge}>
                <Text style={styles.rejectedStatusText}>مرفوض</Text>
              </View>
            )}
            {initiative.approval_status === 'approved' && (
              <View style={styles.approvedStatusBadge}>
                <Text style={styles.approvedStatusText}>معتمد</Text>
              </View>
            )}

            {/* View-only badge for executive users */}
            {isViewOnly && <ViewOnlyBadge style={{ marginTop: 8 }} />}

            <Text style={[styles.pageDescription, { color: themeColors.textMuted }]}>
              {translate(initiative.description) || t('common.noDescription')}
            </Text>

            {/* Organization Badge */}
            {initiative.department_name && (
              <View style={styles.organizationBadge}>
                <Text style={styles.organizationBadgeText}>{translateOrg(initiative.department_name)}</Text>
              </View>
            )}
          </View>
        </View>

        {/* 3-stage approval action panel — DTS UC-03 */}
        {(() => {
          const aStatus = initiative.approval_status;
          const isOwnerSection = isManager
            && initiative.created_by_id === user?.id;
          const canSubmit = (isOwnerSection || user?.is_superuser)
            && (aStatus === 'pending' || aStatus === 'rejected');
          const canDirector = (isDirector || user?.is_superuser)
            && aStatus === 'pending_director';
          const canStrategy = (isStrategyTeam || user?.is_superuser)
            && aStatus === 'pending_strategy';

          if (!canSubmit && !canDirector && !canStrategy) return null;

          return (
            <View style={{
              backgroundColor: themeColors.card,
              borderColor: themeColors.cardBorder,
              borderWidth: 1,
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
              gap: 12,
            }}>
              <Text style={{
                fontSize: 14,
                fontFamily: FONTS.medium,
                color: themeColors.textPrimary,
                textAlign: 'right',
              }}>
                {canSubmit && 'إرسال للاعتماد'}
                {canDirector && 'مراجعة مدير الإدارة'}
                {canStrategy && 'الاعتماد النهائي - فريق الاستراتيجية'}
              </Text>
              <View style={{ flexDirection: 'row-reverse', gap: 8, flexWrap: 'wrap' }}>
                {canSubmit && (
                  <TouchableOpacity
                    disabled={approvalBusy}
                    onPress={() => handleApprovalAction('submit')}
                    style={{
                      paddingVertical: 10,
                      paddingHorizontal: 18,
                      borderRadius: 8,
                      backgroundColor: '#A9211F',
                      opacity: approvalBusy ? 0.6 : 1,
                    }}
                  >
                    <Text style={{ color: '#FFFFFF', fontSize: 14, fontFamily: FONTS.medium }}>
                      إرسال للاعتماد
                    </Text>
                  </TouchableOpacity>
                )}
                {canDirector && (
                  <>
                    <TouchableOpacity
                      disabled={approvalBusy}
                      onPress={() => handleApprovalAction('directorApprove')}
                      style={{
                        paddingVertical: 10,
                        paddingHorizontal: 18,
                        borderRadius: 8,
                        backgroundColor: '#22C55E',
                        opacity: approvalBusy ? 0.6 : 1,
                      }}
                    >
                      <Text style={{ color: '#FFFFFF', fontSize: 14, fontFamily: FONTS.medium }}>
                        اعتماد
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      disabled={approvalBusy}
                      onPress={() => { setRejectModalKind('director'); setRejectComment(''); }}
                      style={{
                        paddingVertical: 10,
                        paddingHorizontal: 18,
                        borderRadius: 8,
                        backgroundColor: '#EF4444',
                        opacity: approvalBusy ? 0.6 : 1,
                      }}
                    >
                      <Text style={{ color: '#FFFFFF', fontSize: 14, fontFamily: FONTS.medium }}>
                        رفض مع سبب
                      </Text>
                    </TouchableOpacity>
                  </>
                )}
                {canStrategy && (
                  <>
                    <TouchableOpacity
                      disabled={approvalBusy}
                      onPress={() => handleApprovalAction('strategyApprove')}
                      style={{
                        paddingVertical: 10,
                        paddingHorizontal: 18,
                        borderRadius: 8,
                        backgroundColor: '#22C55E',
                        opacity: approvalBusy ? 0.6 : 1,
                      }}
                    >
                      <Text style={{ color: '#FFFFFF', fontSize: 14, fontFamily: FONTS.medium }}>
                        اعتماد نهائي
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      disabled={approvalBusy}
                      onPress={() => { setRejectModalKind('strategy'); setRejectComment(''); }}
                      style={{
                        paddingVertical: 10,
                        paddingHorizontal: 18,
                        borderRadius: 8,
                        backgroundColor: '#EF4444',
                        opacity: approvalBusy ? 0.6 : 1,
                      }}
                    >
                      <Text style={{ color: '#FFFFFF', fontSize: 14, fontFamily: FONTS.medium }}>
                        رفض مع سبب
                      </Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>

              {/* Approval timeline — show what already happened */}
              {(initiative.director_reviewed_at || initiative.strategy_reviewed_at) && (
                <View style={{ borderTopWidth: 1, borderTopColor: themeColors.border, paddingTop: 8, gap: 4 }}>
                  {initiative.director_reviewed_at && (
                    <Text style={{ fontSize: 12, color: themeColors.textMuted, textAlign: 'right' }}>
                      مدير الإدارة:
                      {initiative.director_comment ? ` ${initiative.director_comment}` : ' (بدون تعليق)'}
                    </Text>
                  )}
                  {initiative.strategy_reviewed_at && (
                    <Text style={{ fontSize: 12, color: themeColors.textMuted, textAlign: 'right' }}>
                      فريق الاستراتيجية:
                      {initiative.strategy_comment ? ` ${initiative.strategy_comment}` : ' (بدون تعليق)'}
                    </Text>
                  )}
                </View>
              )}
            </View>
          );
        })()}

        {/* Reject-with-reason modal */}
        {rejectModalKind && (
          <View style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}>
            <View style={{
              backgroundColor: themeColors.card,
              borderRadius: 12,
              padding: 20,
              width: '90%',
              maxWidth: 480,
              gap: 12,
            }}>
              <Text style={{ fontSize: 16, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: 'right' }}>
                سبب الرفض
              </Text>
              <TextInput
                value={rejectComment}
                onChangeText={setRejectComment}
                placeholder="اكتب سبب الرفض بشكل واضح..."
                placeholderTextColor={themeColors.textMuted}
                multiline
                style={{
                  borderWidth: 1,
                  borderColor: themeColors.border,
                  borderRadius: 8,
                  padding: 10,
                  minHeight: 100,
                  textAlign: 'right',
                  color: themeColors.textPrimary,
                  backgroundColor: themeColors.backgroundAlt,
                }}
              />
              <View style={{ flexDirection: 'row-reverse', gap: 8 }}>
                <TouchableOpacity
                  onPress={submitRejection}
                  disabled={approvalBusy}
                  style={{
                    paddingVertical: 10,
                    paddingHorizontal: 16,
                    backgroundColor: '#EF4444',
                    borderRadius: 8,
                    opacity: approvalBusy ? 0.6 : 1,
                  }}
                >
                  <Text style={{ color: '#FFFFFF', fontFamily: FONTS.medium }}>تأكيد الرفض</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => { setRejectModalKind(null); setRejectComment(''); }}
                  style={{
                    paddingVertical: 10,
                    paddingHorizontal: 16,
                    borderWidth: 1,
                    borderColor: themeColors.border,
                    borderRadius: 8,
                  }}
                >
                  <Text style={{ color: themeColors.textPrimary, fontFamily: FONTS.medium }}>إلغاء</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        )}

        {/* Pending Deletion Banner */}
        {(initiative as any).pending_deletion_info && (
          <DeletionApprovalBanner
            deletionInfo={(initiative as any).pending_deletion_info}
            canApprove={canApproveDeletion}
            canCancel={isOrgEmployee && (initiative as any).pending_deletion_info.requested_by_id === user?.id}
            onApproved={() => { showSuccess(t('deletion.approved')); navigation.goBack(); }}
            onRejected={() => { showSuccess(t('deletion.rejected')); fetchData(); }}
            onCancelled={() => { showSuccess(t('deletion.requestCancelled')); fetchData(); }}
            onViewImpact={() => setShowDeletionImpact(true)}
          />
        )}

        {/* Info Cards Row - Figma Design */}
        <View style={[
          styles.infoCardsRow,
          { flexDirection: 'row-reverse' },
          isMobile && { flexWrap: 'wrap', gap: 12 }
        ]}>
          {/* Strategic Goal */}
          <View style={[styles.infoCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }, isMobile && { minWidth: '45%' }]}>
            <Text style={[styles.infoCardLabel, { color: colors.textPrimary }]}>{t('initiatives.strategicGoal')}</Text>
            <Text style={[styles.infoCardValue, { color: colors.textMuted }]}>
              {translate(initiative.strategic_goal_title) || '-'}
            </Text>
          </View>

          {/* Implementation Responsibility — DTS cascade: assigned employees
              → responsible_person → creator's section. Backend resolves the
              cascade and returns the final display string in
              ``responsible_display``. We still surface the cooperating
              ``responsible_unit_names`` underneath when available, since they
              represent collaborating sections (a different concept from "who
              is accountable"). */}
          <View style={[styles.infoCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }, isMobile && { minWidth: '45%' }]}>
            <Text style={[styles.infoCardLabel, { color: colors.textPrimary }]}>{t('initiatives.implementationResponsibility')}</Text>
            <Text style={[styles.infoCardValue, { color: colors.textMuted }]}>
              {initiative.responsible_display
                ? translate(initiative.responsible_display)
                : initiative.responsible_unit_names?.length > 0
                ? initiative.responsible_unit_names.map(name => translate(name)).join('\n')
                : '-'}
            </Text>
          </View>

          {/* Start and End Date */}
          <View style={[styles.infoCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }, isMobile && { minWidth: '45%' }]}>
            <Text style={[styles.infoCardLabel, { color: colors.textPrimary }]}>{t('initiatives.startEndDate')}</Text>
            <Text style={[styles.infoCardValue, { color: colors.textMuted }]}>
              {initiative.start_date && initiative.end_date
                ? `${t('common.from')} ${new Date(initiative.start_date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })} ${t('common.to')} ${new Date(initiative.end_date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}`
                : '-'}
            </Text>
          </View>

          {/* Resources */}
          <View style={[styles.infoCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }, isMobile && { minWidth: '45%' }]}>
            <Text style={[styles.infoCardLabel, { color: colors.textPrimary }]}>{t('initiatives.resources')}</Text>
            <Text style={[styles.infoCardValue, { color: colors.textMuted }]}>
              {initiative.resources_description || '-'}
            </Text>
          </View>
        </View>

        {/* Gauge Cards Row - Figma Design */}
        <View style={[
          styles.gaugeCardsRow,
          !isDesktop && { flexDirection: 'column', gap: 16 }
        ]}>
          {/* KPI Status Distribution - Fixed Width 538px */}
          <View style={[
            styles.statusDistributionCard,
            { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder },
            !isDesktop && { maxWidth: '100%' },
            isMobile && { padding: 16 }
          ]}>
            <Text style={[styles.gaugeCardTitleRight, { color: themeColors.textPrimary }]}>{t('kpis.statusDistribution')}</Text>
            <View style={[
              styles.statusDistributionContent,
              isMobile && { flexDirection: 'column', alignItems: 'center', gap: 16 }
            ]}>
              {/* Pie Chart on Left */}
              <View style={styles.donutChartWrapper}>
                <PieChart
                  excellent={excellentPercent}
                  good={goodPercent}
                  needsImprovement={needsImprovementPercent}
                  size={isMobile ? 100 : 120}
                />
              </View>
              {/* Legend on Right */}
              <View style={[
                styles.statusLegend,
                isMobile && { flexDirection: 'row', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }
              ]}>
                <View style={styles.statusLegendItem}>
                  <Text style={[styles.statusLegendPercent, { color: themeColors.textPrimary }]}>% {excellentPercent}</Text>
                  <View style={styles.statusLegendLabelRow}>
                    <Text style={[styles.statusLegendLabel, { color: themeColors.textMuted }]}>{t('status.excellent')}</Text>
                    <View style={[styles.statusLegendBar, { backgroundColor: '#22C55E' }]} />
                  </View>
                </View>
                <View style={styles.statusLegendItem}>
                  <Text style={[styles.statusLegendPercent, { color: themeColors.textPrimary }]}>% {goodPercent}</Text>
                  <View style={styles.statusLegendLabelRow}>
                    <Text style={[styles.statusLegendLabel, { color: themeColors.textMuted }]}>{t('status.good')}</Text>
                    <View style={[styles.statusLegendBar, { backgroundColor: '#F97316' }]} />
                  </View>
                </View>
                <View style={styles.statusLegendItem}>
                  <Text style={[styles.statusLegendPercent, { color: themeColors.textPrimary }]}>% {needsImprovementPercent}</Text>
                  <View style={styles.statusLegendLabelRow}>
                    <Text style={[styles.statusLegendLabel, { color: themeColors.textMuted }]}>{t('status.needsImprovement')}</Text>
                    <View style={[styles.statusLegendBar, { backgroundColor: '#EF4444' }]} />
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* Right Cards Container - Flex 1 */}
          <View style={[
            styles.gaugeCardsRight,
            !isDesktop && { flexDirection: 'column', gap: 16, flex: 0 }
          ]}>
            {/* Deviation Rate */}
            <View style={[
              styles.gaugeCardFlex,
              { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder },
              !isDesktop && { flex: 0, width: '100%', minWidth: 'auto', minHeight: 160 }
            ]}>
              <Text style={[styles.gaugeCardTitleRight, { color: themeColors.textPrimary }]}>{t('initiatives.deviationPercentage')}</Text>
              {deviation != null ? (
                <View style={styles.gaugeContainer}>
                  <CircularGauge
                    percentage={deviation}
                    size={isMobile ? 100 : 120}
                    strokeWidth={isMobile ? 10 : 12}
                    color="#FF7F0E"
                    backgroundColor={isDark ? '#374151' : '#E5E7EB'}
                  />
                </View>
              ) : (
                <View style={[styles.gaugeContainer, { justifyContent: 'center', alignItems: 'center', minHeight: isMobile ? 100 : 120 }]}>
                  <Text style={{ fontSize: 28, fontWeight: '700', color: themeColors.textMuted }}>--</Text>
                </View>
              )}
            </View>

            {/* Performance KPIs */}
            <View style={[
              styles.gaugeCardFlex,
              { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder },
              !isDesktop && { flex: 0, width: '100%', minWidth: 'auto', minHeight: 160 }
            ]}>
              <Text style={[styles.gaugeCardTitleRight, { color: themeColors.textPrimary }]}>{t('kpis.performanceIndicators')}</Text>
              {avgPerformance != null ? (
                <View style={styles.gaugeContainer}>
                  <CircularGauge
                    percentage={avgPerformance}
                    size={isMobile ? 100 : 120}
                    strokeWidth={isMobile ? 10 : 12}
                    color={getAchievementColor(avgPerformance)}
                    backgroundColor={isDark ? '#374151' : '#E5E7EB'}
                  />
                </View>
              ) : (
                <View style={[styles.gaugeContainer, { justifyContent: 'center', alignItems: 'center', minHeight: isMobile ? 100 : 120 }]}>
                  <Text style={{ fontSize: 28, fontWeight: '700', color: themeColors.textMuted }}>--</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Bottom Section - Figma Design (3-column grid) */}
        <View style={[
          styles.bottomSectionGrid,
          !isDesktop && { flexDirection: 'column', gap: 16 }
        ]}>
          {/* Right Column: Initiative Links (spans 2 columns) */}
          <View style={[
            styles.linksColumnFigma,
            !isDesktop && { flex: 0, width: '100%' }
          ]}>
            <View style={[styles.linksCardContainer, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              {/* Card Header */}
              <Text style={[styles.linksCardTitle, { color: themeColors.textPrimary }]}>{t('initiatives.initiativeLinks')}</Text>

              {/* Performance KPIs Section */}
              <View style={[styles.linksSectionGray, { backgroundColor: themeColors.backgroundAlt }]}>
                <View style={styles.linksSectionHeaderRow}>
                  {/* Add KPI Button - Hidden for strategy_team and view-only users */}
                  {(canCreateOperationalItem || (!isStrategyTeam && !isViewOnly)) && (
                    <TouchableOpacity
                      style={[styles.addButtonFigma, { minHeight: 44 }]}
                      onPress={() => navigation.navigate('AddInitiative', { initiativeId: id, scrollToKpi: true })}
                    >
                      <Text style={styles.addButtonText}>{t('kpis.addIndicator')}</Text>
                      <PlusIcon size={13} color={themeColors.iconDefault} />
                    </TouchableOpacity>
                  )}
                  <Text style={[styles.linksSectionTitleText, { color: themeColors.textPrimary }]}>{t('kpis.performanceIndicators')}</Text>
                </View>

                <View style={styles.rtlWrapper}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    contentContainerStyle={styles.horizontalCardsRow}
                    style={styles.horizontalScrollContainer}
                  >
                    {kpis.length > 0 ? (
                      kpis.map((kpi) => (
                        <View key={kpi.id} style={styles.rtlCard}>
                          <KPICard
                            id={kpi.id}
                            title={kpi.name}
                            achievementPercentage={kpi.achievement_percentage || 0}
                            weight={kpi.weight}
                            cardWidth={horizontalCardWidth}
                            onViewDetails={handleKPIViewDetails}
                            t={t}
                            translate={translate}
                          />
                        </View>
                      ))
                    ) : (
                      <View style={[styles.emptyStateContainer, styles.rtlCard]}>
                        <Text style={styles.emptyStateText}>{t('initiatives.noLinkedKPIs')}</Text>
                      </View>
                    )}
                  </ScrollView>
                </View>
              </View>

              {/* DTS UC-03 step 4 — strategic KPIs this initiative contributes
                  to, with the per-link weight that drives the aggregate-source
                  achievement engine (DTS 4.3 level 4b). */}
              {Array.isArray((initiative as any).kpi_contributions) && (initiative as any).kpi_contributions.length > 0 && (
                <View style={[styles.linksSectionGray, { backgroundColor: themeColors.backgroundAlt }]}>
                  <View style={styles.linksSectionHeaderRow}>
                    <Text style={[styles.linksSectionTitleText, { color: themeColors.textPrimary }]}>إسهامات هذه المبادرة في المؤشرات الاستراتيجية</Text>
                  </View>
                  <View style={{ paddingHorizontal: 12, paddingBottom: 12, gap: 8 }}>
                    {(initiative as any).kpi_contributions.map((row: any) => (
                      <View
                        key={row.kpi}
                        style={{
                          flexDirection: 'row-reverse',
                          alignItems: 'center',
                          gap: 12,
                          paddingHorizontal: 12,
                          paddingVertical: 10,
                          borderRadius: 8,
                          backgroundColor: themeColors.card,
                          borderWidth: 1,
                          borderColor: themeColors.cardBorder,
                        }}
                      >
                        <View style={{ flex: 1 }}>
                          <Text
                            style={{
                              fontSize: 14,
                              fontFamily: FONTS.medium,
                              color: themeColors.textPrimary,
                              textAlign: 'right',
                            }}
                            numberOfLines={1}
                          >
                            {translate(row.kpi_name) || `مؤشر #${row.kpi}`}
                          </Text>
                        </View>
                        <View
                          style={{
                            paddingHorizontal: 12,
                            paddingVertical: 6,
                            borderRadius: 6,
                            backgroundColor: isDark ? 'rgba(169, 33, 31, 0.15)' : '#FEF2F2',
                          }}
                        >
                          <Text
                            style={{
                              color: '#A9211F',
                              fontSize: 13,
                              fontFamily: FONTS.medium,
                            }}
                          >
                            وزن {Number(row.weight).toFixed(2)}%
                          </Text>
                        </View>
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Activities Section */}
              <View style={[styles.linksSectionGray, { backgroundColor: themeColors.backgroundAlt }]}>
                <View style={styles.linksSectionHeaderRow}>
                  {/* Add Activity Button - Hidden for strategy_team and view-only users */}
                  {(canCreateOperationalItem || (!isStrategyTeam && !isViewOnly)) && (
                    <TouchableOpacity
                      style={[styles.addButtonFigma, { minHeight: 44 }]}
                      onPress={() => navigation.navigate('AddActivity', { initiativeId: id })}
                    >
                      <Text style={styles.addButtonText}>{t('initiatives.addActivity')}</Text>
                      <PlusIcon size={13} color={themeColors.iconDefault} />
                    </TouchableOpacity>
                  )}
                  <Text style={[styles.linksSectionTitleText, { color: themeColors.textPrimary }]}>{t('initiatives.activities')}</Text>
                </View>

                <View style={styles.rtlWrapper}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    contentContainerStyle={styles.horizontalCardsRow}
                    style={styles.horizontalScrollContainer}
                  >
                    {activities.length > 0 ? (
                      activities.map((activity) => (
                        <View key={activity.id} style={styles.rtlCard}>
                          <ActivityCard
                            activity={activity}
                            cardWidth={horizontalCardWidth}
                            onViewDetails={handleActivityViewDetails}
                            t={t}
                            translate={translate}
                          />
                        </View>
                      ))
                    ) : (
                      <View style={[styles.emptyStateContainer, styles.rtlCard]}>
                        <Text style={styles.emptyStateText}>{t('initiatives.noLinkedActivities')}</Text>
                      </View>
                    )}
                  </ScrollView>
                </View>
              </View>
            </View>
          </View>

          {/* Left Column: Comments - Unified with GoalDetailScreen */}
          <View style={[
            styles.commentsColumn,
            !isDesktop && { flex: 0, width: '100%' }
          ]}>
            <CommentSection
              title={t('comments.title')}
              comments={comments}
              showAddComment={showAddComment}
              editingComment={editingComment}
              hideAddButton={!canComment}
              onAddClick={() => setShowAddComment(true)}
              onEditComment={handleEditComment}
              onDeleteComment={handleDeleteComment}
              onSubmitComment={handleCommentSubmit}
              onCancelComment={handleCancelComment}
            />
          </View>
        </View>
      </ScrollView>

      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => setShowDeletionImpact(false)}
        onConfirm={confirmDeleteInitiative}
        entityName={initiative?.title || ''}
        fetchImpact={async () => { const r = await initiativesApi.deletionImpact(id); return r.data; }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={isDeleting}
      />

    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
  },
  contentSmall: {
    padding: 12,
  },
  // Breadcrumb
  breadcrumbRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: 16,
    gap: 8,
  },
  breadcrumbRowSmall: {
    flexWrap: 'wrap',
    gap: 4,
  },
  breadcrumbLink: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbSeparator: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbCurrent: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Title Section Card - Figma Design
  titleSectionCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 20,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    gap: 8,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'hidden',
  },
  editIconButton: {
    backgroundColor: 'rgba(51, 65, 85, 0.05)',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    minWidth: 32,
    minHeight: 32,
    padding: 7,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Pending Status Badge
  pendingStatusBadge: {
    backgroundColor: '#9467BD',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 40,
    alignSelf: 'flex-end',
  },
  pendingStatusText: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  draftStatusBadge: {
    backgroundColor: '#6B7280',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 40,
    alignSelf: 'flex-end',
  },
  draftStatusText: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  rejectedStatusBadge: {
    backgroundColor: '#EF4444',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 40,
    alignSelf: 'flex-end',
  },
  rejectedStatusText: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  approvedStatusBadge: {
    backgroundColor: '#22C55E',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 40,
    alignSelf: 'flex-end',
  },
  approvedStatusText: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#3B82F6',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    minHeight: 40,
  },
  submitButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  // Organization Badge
  organizationBadge: {
    backgroundColor: '#FEF3C7',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
    alignSelf: 'flex-end',
  },
  organizationBadgeText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#92400E',
  },
  titleTextContainer: {
    flex: 1,
    gap: 8,
    alignItems: 'flex-end',
    flexShrink: 1,
    minWidth: 0,
  },
  pageTitle: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 26,
    width: '100%',
  },
  pageDescription: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
    lineHeight: 24,
    width: '100%',
    flexShrink: 1,
  },
  // Info Cards Row - Figma Design
  infoCardsRow: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 20,
  },
  infoCardsRowSmall: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  infoCard: {
    flex: 1,
    minWidth: 140,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    alignItems: 'flex-end',
    gap: 4,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'hidden',
  },
  infoCardLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  infoCardValue: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
    lineHeight: 14,
  },
  // Gauge Cards Row - Figma Design
  gaugeCardsRow: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 20,
  },
  gaugeCardsRowSmall: {
    flexDirection: 'column',
    gap: 16,
  },
  // Status Distribution Card - Fixed Width
  statusDistributionCard: {
    width: '100%',
    maxWidth: 538,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 20,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    gap: 20,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'hidden',
  },
  statusDistributionCardSmall: {
    maxWidth: '100%',
    padding: 16,
  },
  statusDistributionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  donutChartWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Status Legend
  statusLegend: {
    gap: 24,
  },
  statusLegendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statusLegendPercent: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    color: '#303030',
    textAlign: 'right',
  },
  statusLegendLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },
  statusLegendLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
    width: 72,
    textAlign: 'right',
  },
  statusLegendBar: {
    width: 8,
    height: 32,
    borderRadius: 2,
  },
  // Right Cards Container
  gaugeCardsRight: {
    flex: 1,
    flexDirection: 'row',
    gap: 20,
  },
  gaugeCardsRightSmall: {
    flexDirection: 'column',
    gap: 16,
  },
  gaugeCardFlex: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 20,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    minWidth: 200,
  },
  gaugeCardFlexSmall: {
    flex: 0,
    width: '100%',
    minWidth: 'auto',
  },
  gaugeCardTitleRight: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    width: '100%',
    lineHeight: 24,
  },
  gaugeContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
  },
  // Bottom Section - Figma Design (3-column grid layout)
  bottomSectionGrid: {
    flexDirection: 'row-reverse', // RTL: Comments on left, Links on right
    gap: 20,
  },
  bottomSectionGridSmall: {
    flexDirection: 'column',
    gap: 16,
  },
  // Links Column (spans 2 columns in Figma)
  linksColumnFigma: {
    flex: 2,
  },
  linksColumnFigmaSmall: {
    flex: 0,
    width: '100%',
  },
  linksCardContainer: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    gap: 16,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  linksCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  linksSectionGray: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 12,
    gap: 10,
  },
  linksSectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  linksSectionTitleText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    width: '100%',
    textAlign: 'right',
    writingDirection: 'rtl',
    lineHeight: 20,
  },
  addButtonFigma: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingHorizontal: 12,
    paddingVertical: 5.5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    minHeight: 32,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  addButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    lineHeight: 20,
  },
  // Comments Column - Unified with GoalDetailScreen
  commentsColumn: {
    flex: 1,
  },
  commentsColumnSmall: {
    flex: 0,
    width: '100%',
  },
  // KPI Cards Grid
  // Horizontal scroll container for cards
  horizontalScrollContainer: {
    flexGrow: 0,
  },
  // RTL wrapper - flips the entire ScrollView horizontally so cards start from right
  rtlWrapper: {
    transform: [{ scaleX: -1 }],
  },
  // RTL card - flips each card back to normal orientation
  rtlCard: {
    transform: [{ scaleX: -1 }],
  },
  horizontalCardsRow: {
    flexDirection: 'row',
    gap: 16,
    paddingHorizontal: 4,
    paddingBottom: 12,
  },
  // KPI Card
  kpiCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 12,
    width: 288, // Fixed width (w-72)
    flexShrink: 0, // Prevent shrinking
    borderWidth: 1,
    borderColor: '#E2E8F0',
    gap: 16,
    overflow: 'hidden',
  },
  kpiCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  kpiWeightRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 6,
    marginBottom: 4,
  },
  kpiWeightLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  kpiWeightBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  kpiWeightValue: {
    fontSize: 12,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
  },
  kpiValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  kpiValueBadge: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 100,
  },
  kpiValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#414042',
  },
  kpiProgressContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  kpiProgressTrack: {
    height: 8,
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    position: 'relative',
    overflow: 'visible',
  },
  kpiProgressFill: {
    position: 'absolute',
    right: 0,
    top: 0,
    height: '100%',
    borderRadius: 8,
  },
  kpiProgressDot: {
    position: 'absolute',
    width: 14,
    height: 14,
    borderRadius: 7,
    top: -3,
    borderWidth: 2,
    borderColor: COLORS.white,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.2)',
    elevation: 2,
  },
  kpiDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
    minHeight: 24,
  },
  kpiDetailsText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Activity Card
  activityCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 12,
    width: 288, // Fixed width (w-72)
    flexShrink: 0, // Prevent shrinking
    borderWidth: 1,
    borderColor: '#E2E8F0',
    gap: 16,
    overflow: 'hidden',
  },
  activityCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  // Empty State
  emptyStateContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 24,
    minWidth: '100%',
  },
  emptyStateText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
    textAlign: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    fontWeight: '600',
    color: '#1F2937',
    textAlign: 'right',
    marginBottom: 8,
  },
  modalMessage: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right',
    marginBottom: 16,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
  },
  modalCancelButton: {
    flex: 1,
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
  modalCancelButtonText: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: '#6B7280',
  },
  modalRejectButton: {
    flex: 1,
    backgroundColor: '#EF4444',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
  modalRejectButtonText: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: '#FFFFFF',
  },
  rejectionReasonInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    marginVertical: 16,
    minHeight: 100,
    fontFamily: FONTS.regular,
    fontSize: 14,
    textAlignVertical: 'top',
  },
});
